import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Loader, Button } from '@mantine/core';
import { useTodos, useTodoLists, useUpdateTodo, useDeleteTodo, useUsers } from '../hooks/useTodos';
import { todoApi } from '../api/todoApi';
import { useReorderTodos } from '../hooks/useReorderTodos';
import { useSignalR } from '../contexts/SignalRContext';
import { 
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  MeasuringStrategy,
  DragOverlay,
} from '@dnd-kit/core';
import { 
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { TodoItem } from '../types/todo';
import { UpdateTodoRequest } from '../types/todo';

import { SuperTaskToolbar } from './SuperTaskToolbar';
import { EditTodoForm } from './EditTodoForm';
import { CreateListForm } from './CreateListForm';
import { EditListForm } from './EditListForm';
import { ListInfoDialog } from './ListInfoDialog';
import { Modal } from './Modal';
import { SectionTodoEntry } from './SectionTodoEntry';
import { TodoItemComponent } from './TodoItem';
import { useUser } from '../contexts/UserContext';
import { groupTodosByDueDate } from '../utils/dateUtils';
import { SortType } from './SortDropdown';
import { getListColorByValue } from '../utils/colorUtils';
import { CheckCircle2, Check, Trash2, X } from 'lucide-react';
import { TodoSectionWrapper } from './TodoSectionWrapper';
import { usePersistedState } from '../hooks/usePersistedState';
import './TodoList.css';
import { getTodayISO, getYesterdayISO, getTomorrowISO, isCompletedToday, getThisWeekendISO, getRandomLaterDate } from '../utils/dateUtils';

interface TodoListProps {
  filters?: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string };
  onFiltersChange?: (filters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => void;
  highlightedTaskId?: number | null;
  onTaskInteraction?: () => void;
  highlightMessage?: string;
  onTodoUpdate?: (taskId: number, message?: string) => void;
  pendingHighlight?: {taskId: number, message: string} | null;
  onApplyPendingHighlight?: () => void;
  hideToolbar?: boolean;
  onNewTodoCreated?: (taskId: number, title: string) => void;
}
export const TodoList: React.FC<TodoListProps> = (props) => {
  // All hooks must be called before any return or conditional
  // Defensive: always initialize all hooks in the same order, regardless of props
  // Use props.filters, props.onFiltersChange below
  const onFiltersChange = props.onFiltersChange;
  const highlightedTaskId = props.highlightedTaskId;
  const onTaskInteraction = props.onTaskInteraction;
  const highlightMessage = props.highlightMessage;
  const onTodoUpdate = props.onTodoUpdate;
  const pendingHighlight = props.pendingHighlight;
  const onApplyPendingHighlight = props.onApplyPendingHighlight;

  const [hasLoaded, setHasLoaded] = useState(false);
  const { data: todos, isLoading, error } = useTodos();
  const { data: todoLists = [] } = useTodoLists();
  const { data: users = [] } = useUsers();
  const { currentUser } = useUser();
  const { joinListGroup, isConnected } = useSignalR();
  const [localFilters, setLocalFilters] = useState({ assignee: 'any', list: 'all' as string | number, completed: 'hidden' as 'today' | 'all' | 'hidden', tags: [] as string[], search: '' });
  // Always define filters, even if externalFilters is undefined
  const filters = typeof props.filters === 'object' && props.filters !== null
    ? props.filters
    : localFilters;
  const [sortType, setSortType] = usePersistedState<SortType>('symphonytodo-sort', 'rank');
  const [showCreateListForm, setShowCreateListForm] = useState(false);
  const [showEditListForm, setShowEditListForm] = useState(false);
  const [showListInfoDialog, setShowListInfoDialog] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoItem | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateTodoRequest | null>(null);
  // Need to get queryClient for the mutations
  const queryClient = useQueryClient();
  
  const [createListData, setCreateListData] = useState<{ name: string; description?: string; color?: string } | null>(null);
  const [editListData, setEditListData] = useState<{ name: string; description?: string; color?: string; isShared?: boolean; sharedWith?: string[]; isPublic?: boolean } | null>(null);
  const [newPublicUrl, setNewPublicUrl] = useState<string | null>(null);

  // Create list mutation
  const createListMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; color?: string }) => 
      todoApi.createList(data, currentUser?.email || ''),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowCreateListForm(false);
      setCreateListData(null);
      if (newList && newList.id) {
        if (onFiltersChange) {
          onFiltersChange({ ...filters, list: newList.id });
        } else {
          setLocalFilters(f => ({ ...f, list: newList.id }));
        }
        // Focus the quick add input after creating a new list (desktop only)
        setTimeout(() => {
          if (window.innerWidth > 768) {
            const quickAddInput = document.querySelector('.main-input') as HTMLInputElement | null;
            if (quickAddInput) {
              quickAddInput.focus();
            }
          }
        }, 300);
      }
    },
  });

  // Update list mutation
  const updateListMutation = useMutation({
    mutationFn: async (data: { 
      name: string; 
      description?: string; 
      color?: string;
      isShared?: boolean;
      sharedWith?: string[];
      isPublic?: boolean;
    }) => {
      if (!selectedList) return null;
      
      // First update basic list info including public sharing
      await todoApi.updateList(selectedList.id, {
        name: data.name,
        description: data.description,
        color: data.color,
        isPublic: data.isPublic
      }, currentUser?.email || '');
      
      // Then update sharing if it changed
      const sharingChanged = data.isShared !== selectedList.isShared || 
        JSON.stringify(data.sharedWith) !== JSON.stringify(selectedList.sharedWith);
        
      if (sharingChanged) {
        await todoApi.updateListSharing(
          selectedList.id, 
          data.isShared || false, 
          data.sharedWith || [], 
          currentUser?.email || ''
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowEditListForm(false);
      setEditListData(null);
    },
  });

  // Delete list mutation
  const deleteListMutation = useMutation({
    mutationFn: () => {
      if (!selectedList) return Promise.resolve();
      return todoApi.deleteList(selectedList.id, currentUser?.email || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowEditListForm(false);
      setEditListData(null);
    },
  });

  // Use props for highlighting instead of local state
  const reorderMutation = useReorderTodos();
  // Track last dropped todo to suppress animation
  const [lastDroppedId, setLastDroppedId] = useState<number | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dragOverInfo, setDragOverInfo] = useState<{ sectionId: string; index: number } | null>(null);
  const updateTodo = useUpdateTodo();
  const deleteTodo = useDeleteTodo();

  // Configure sensors for better mobile scroll support.
  // Pointer events generally play nicer with native scrolling than the TouchSensor,
  // especially when only the dedicated drag handle is draggable.
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      // On mobile, a quick swipe should scroll, not start a drag.
      // Long-press then move to reorder.
      delay: 200,
      tolerance: 8,
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });

  // On mobile, DnD sensors can intercept touch gestures and completely block scrolling.
  // Keep drag-and-drop enabled on desktop; on mobile, disable touch/pointer sensors so the
  // list remains natively scrollable.
  // Note: match Mantine's `md` breakpoint (used elsewhere for mobile layout).
  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 991;
  const sensors = useSensors(...(isMobileViewport ? [keyboardSensor] : [pointerSensor, keyboardSensor]));

  // dnd-kit can still interfere with touch scrolling on some mobile Chrome/Android builds
  // even when sensors are minimized. Keep DnD entirely desktop-only.
  const enableDnd = !isMobileViewport;

  // Custom measuring configuration to prevent auto-sorting during drag
  const measuring = {
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  };

  // Clear lastDroppedId after one render - extended timeout for cross-section moves
  useEffect(() => {
    if (lastDroppedId !== null) {
      const timeout = setTimeout(() => setLastDroppedId(null), 300); // Increased from 50ms to 300ms
      return () => clearTimeout(timeout);
    }
  }, [lastDroppedId]);

  // Track loaded state
  useEffect(() => {
    if (!hasLoaded && !isLoading && todos && Array.isArray(todos)) {
      setHasLoaded(true);
    }
  }, [hasLoaded, isLoading, todos]);

  // Apply pending highlight when data updates and task is found
  useEffect(() => {
    if (pendingHighlight && onApplyPendingHighlight && todos && Array.isArray(todos)) {
      // Check if the task with pending highlight now exists in the data
      const taskExists = todos.some(todo => todo.id === pendingHighlight.taskId);
      if (taskExists) {
        // Apply the highlight now that the task has been re-rendered in its new location
        onApplyPendingHighlight();
      }
    }
  }, [pendingHighlight, onApplyPendingHighlight, todos]);

  // Join SignalR groups for all user's lists when connected
  useEffect(() => {
    if (isConnected && todoLists.length > 0) {
      todoLists.forEach(list => {
        joinListGroup(list.id).catch(error => {
          console.error(`Failed to join group for list ${list.id}:`, error);
        });
      });
    }
  }, [isConnected, todoLists, joinListGroup]);

  // Always render the component, but show loading or error UI conditionally

  // ...existing code for useEffect, useMemo, handlers, and render...

  const selectedList = useMemo(() => {
    if (typeof filters.list === 'number') {
      return todoLists.find(list => list.id === filters.list);
    }
    return null;
  }, [filters.list, todoLists]);

  const listTheme = useMemo(() => {
    if (selectedList?.color) {
      return getListColorByValue(selectedList.color);
    }
    return null;
  }, [selectedList]);

  const handleFilterChange = (newFilters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => {
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    } else {
      setLocalFilters(newFilters);
    }
  };

  const handleEditList = () => {
    if (selectedList && currentUser) {
      const isOwner = currentUser.email === selectedList.createdBy;
      if (isOwner) {
        setShowEditListForm(true);
      } else {
        setShowListInfoDialog(true);
      }
    }
  };

  // Create a unique wrapper function with identifier
  const uniqueEditListWrapper = () => {
    handleEditList();
  };

  // Ensure todos is an array before filtering
  const todoArray = Array.isArray(todos) ? todos : [];
  const filteredTodos = todoArray.filter((todo: TodoItem) => {
    // ...existing filter logic...
    if (filters.assignee !== 'any') {
      if (filters.assignee === 'unassigned') {
        if (todo.assignedTo && todo.assignedTo.trim() !== '') {
          return false;
        }
      } else {
        const targetAssignee = (currentUser && filters.assignee === currentUser.email) 
          ? currentUser.email 
          : filters.assignee;
        if (!todo.assignedTo || todo.assignedTo !== targetAssignee) {
          return false;
        }
      }
    }
    if (filters.list !== 'all') {
      if (typeof filters.list === 'number') {
        if (todo.listId !== filters.list) {
          return false;
        }
      }
    }
    
    // Apply tags filter
    if (filters.tags && filters.tags.length > 0) {
      let todoTags: string[] = [];
      if (Array.isArray(todo.tags)) {
        todoTags = todo.tags;
      } else if (typeof todo.tags === 'string' && todo.tags) {
        todoTags = (todo.tags as string).split(',').map((t: string) => t.trim().toLowerCase());
      }
      
      // Check if todo has at least one of the selected tags
      const hasMatchingTag = filters.tags.some(filterTag => 
        todoTags.includes(filterTag.toLowerCase())
      );
      
      if (!hasMatchingTag) {
        return false;
      }
    }
    
    // Apply search filter
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase().trim();
      const titleMatch = todo.title.toLowerCase().includes(searchTerm);
      const descriptionMatch = todo.description?.toLowerCase().includes(searchTerm) || false;
      const commentsMatch = todo.comments?.some(comment => 
        comment.content.toLowerCase().includes(searchTerm)
      ) || false;
      
      if (!titleMatch && !descriptionMatch && !commentsMatch) {
        return false;
      }
    }
    
    return true;
  });
  // Only include todos in one section: active or completed, never both
  const allCompletedTodos = filteredTodos.filter((todo: TodoItem) => todo.isCompleted);
  
  // Apply completed filter
  let completedTodos: TodoItem[] = [];
  if (filters.completed === 'all') {
    completedTodos = allCompletedTodos;
  } else if (filters.completed === 'today') {
    completedTodos = allCompletedTodos.filter((todo: TodoItem) => 
      todo.completedAt && isCompletedToday(todo.completedAt)
    );
  }
  // If filters.completed === 'hidden', completedTodos remains empty
  
  const activeTodos = filteredTodos.filter((todo: TodoItem) => !todo.isCompleted);
  // Remove any completed todos from activeTodos (defensive, in case of data issues)
  const completedIds = new Set(allCompletedTodos.map((t: TodoItem) => t.id));
  const trulyActiveTodos = activeTodos.filter((t: TodoItem) => !completedIds.has(t.id));
  const groupedTodos = groupTodosByDueDate(trulyActiveTodos, sortType, completedTodos);

  // Check if there are active filters
  const hasActiveFilters = useMemo(() => {
    return (
      filters.assignee !== 'any' ||
      (filters.list !== 'all' && filters.list !== null && filters.list !== undefined) ||
      (filters.tags && filters.tags.length > 0) ||
      (filters.search && filters.search.trim() !== '')
    );
  }, [filters]);

  // Generate filter summary with styled components
  const getFilterSummaryJSX = useMemo(() => {
    if (!hasActiveFilters) return null;

    const selectedListName = selectedList?.name || 'All lists';

    // If no specific filters but list is selected, simple case
    if (filters.assignee === 'any' && !filters.search && (!filters.tags || filters.tags.length === 0) && filters.list !== 'all') {
      return (
        <span>
          Your <span style={{ fontWeight: 600, color: 'var(--list-color, #3b82f6)' }}>{selectedListName}</span> list is empty
        </span>
      );
    }

    const parts: JSX.Element[] = [];

    // Add assignee filter
    if (filters.assignee !== 'any') {
      if (filters.assignee === 'unassigned') {
        parts.push(
          <span key="assignee">you have no unassigned tasks</span>
        );
      } else {
        const assigneeName = filters.assignee === currentUser?.email ? 'you' : 
          users.find(u => u.email === filters.assignee)?.name?.split(' ')[0] || filters.assignee.split('@')[0];
        parts.push(
          <span key="assignee">you have no tasks assigned to <span style={{ fontWeight: 600, color: '#3b82f6', background: 'rgba(59, 130, 246, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>{assigneeName}</span></span>
        );
      }
    }

    // Add search filter
    if (filters.search && filters.search.trim() !== '') {
      parts.push(
        <span key="search">you searched for <span style={{ fontWeight: 600, color: '#059669', background: 'rgba(5, 150, 105, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>"{filters.search.trim()}"</span></span>
      );
    }

    // Add tags filter
    if (filters.tags && filters.tags.length > 0) {
      const tagElements = filters.tags.map((tag, index) => (
        <span key={tag} style={{ fontWeight: 600, color: '#dc2626', background: 'rgba(220, 38, 38, 0.1)', padding: '2px 6px', borderRadius: '4px', marginRight: index < filters.tags.length - 1 ? '4px' : '0' }}>
          "{tag}"
        </span>
      ));
      
      const tagText = filters.tags.length === 1 ? 
        <span key="tags">you have the {tagElements[0]} label applied</span> :
        <span key="tags">you have these labels applied: {tagElements}</span>;
      parts.push(tagText);
    }

    return (
      <span>
        In <span style={{ fontWeight: 600, color: 'var(--list-color, #3b82f6)' }}>{selectedListName}</span>
        {parts.length > 0 && (
          <>
            {', '}
            {parts.reduce((acc, part, index) => {
              if (index === 0) return [part];
              if (index === parts.length - 1) return [...acc, ', and ', part];
              return [...acc, ', ', part];
            }, [] as (JSX.Element | string)[])}
          </>
        )}
      </span>
    );
  }, [hasActiveFilters, selectedList, filters, currentUser, users]);

  // Clear all filters
  const handleClearFilters = () => {
    if (onFiltersChange) {
      onFiltersChange({
        assignee: 'any',
        list: 'all',
        completed: filters.completed, // Keep completed filter as is
        tags: [],
        search: ''
      });
    } else {
      setLocalFilters({
        assignee: 'any',
        list: 'all',
        completed: filters.completed,
        tags: [],
        search: ''
      });
    }
  };

  // Apply list theme to container - solid background like settings
  const containerStyle = {};

  // Also set CSS custom properties for use in CSS
  const containerProps = listTheme ? {
    '--list-color': listTheme.value,
    '--list-light-color': listTheme.light,
  } as React.CSSProperties : {};

  // Handle drag start for better mobile sensitivity  
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
    
    // Add haptic feedback for mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    
    // Do not lock body scrolling here; on iOS/PWAs it can break nested scrolling.
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    
    if (!over) {
      setDragOverInfo(null);
      return;
    }
    
    // For now, just track which section we're over
    // In the future, we could calculate exact index based on mouse position
    setDragOverInfo({
      sectionId: String(over.id),
      index: 0, // Simplified - could be enhanced to calculate actual insertion index
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    // Clear drag over info.
    setDragOverInfo(null);

    if (!over) {
      setActiveDragId(null); // Clear here for cancelled drags
      return;
    }
    const activeId = Number(active.id);
    const overId = String(over.id);
    
    // Find which section the dragged todo is currently in
    const findTodoInSections = (todoId: number) => {
      const sectionKeys: (keyof typeof groupedTodos)[] = ['overdue', 'today', 'tomorrow', 'thisWeekend', 'later', 'noDueDate', 'completed'];
      for (const sectionKey of sectionKeys) {
        const sectionTodos = groupedTodos[sectionKey] || [];
        const todoIndex = sectionTodos.findIndex(t => t.id === todoId);
        if (todoIndex !== -1) {
          return { sectionKey, todoIndex, todo: sectionTodos[todoIndex] };
        }
      }
      return null;
    };

    const draggedTodoInfo = findTodoInSections(activeId);
    if (!draggedTodoInfo) {
      setActiveDragId(null);
      return;
    }

    // If moving between sections (over.id is a section name)
    if (overId !== draggedTodoInfo.sectionKey && ['overdue', 'today', 'tomorrow', 'thisWeekend', 'later', 'noDueDate', 'completed'].includes(overId)) {
      
      // Clear activeDragId IMMEDIATELY to prevent any snap-back animation
      // This must happen before any async operations
      setActiveDragId(null);
      
      const destKey = overId as keyof typeof groupedTodos;
      const moved = draggedTodoInfo.todo;
      
        // Handle date changes for all sections except completed
        if (destKey !== 'completed') {
          let dueDate: string | null = null;
          if (destKey === 'today') dueDate = getTodayISO();
          else if (destKey === 'tomorrow') dueDate = getTomorrowISO();
          else if (destKey === 'thisWeekend') dueDate = getThisWeekendISO();
          else if (destKey === 'overdue') dueDate = getYesterdayISO();
          else if (destKey === 'later') {
            dueDate = getRandomLaterDate(); // Use random date 8-30 days out to ensure it's after weekend
          }
          else if (destKey === 'noDueDate') dueDate = null;
          else dueDate = moved.dueDate || null;
          
        // Only update if due date would change
        if (dueDate !== (moved.dueDate || null)) {
          updateTodo.mutate({ id: moved.id, data: { dueDate } });
        }
      }
      
      // Handle completion status change
      if (destKey === 'completed' && !moved.isCompleted) {
        updateTodo.mutate({ id: moved.id, data: { isCompleted: true } });
      } else if (destKey !== 'completed' && moved.isCompleted) {
        updateTodo.mutate({ id: moved.id, data: { isCompleted: false } });
      }
      
      // Set last dropped to prevent snap-back animation
      setLastDroppedId(moved.id);
      setSuccessfulMoveId(moved.id);
      return;
    }
    
    // Reordering within the same section (over.id is another todo ID)
    const overTodoInfo = findTodoInSections(Number(overId));
    
    if (!overTodoInfo || overTodoInfo.sectionKey !== draggedTodoInfo.sectionKey) {
      setActiveDragId(null);
      return; // Can't reorder between different sections this way
    }

    const sectionTodos = groupedTodos[draggedTodoInfo.sectionKey] || [];
    if (!sectionTodos.length || sortType !== 'rank') {
      setActiveDragId(null);
      return; // nothing to reorder or not sorting by rank
    }
    
    const oldIndex = draggedTodoInfo.todoIndex;
    const newIndex = overTodoInfo.todoIndex;
    
    if (oldIndex === newIndex) {
      setActiveDragId(null);
      return; // No change
    }

    const updated = arrayMove(sectionTodos, oldIndex, newIndex);
    const moved = draggedTodoInfo.todo;
    
    // Track last dropped todo for animation suppression
    setLastDroppedId(moved.id);
    
    // Only update rank locally for the reordered array
    const reorderedWithRanks = updated.map((todo, i) => ({ ...todo, rank: i + 1 }));
    
    const todoIds = reorderedWithRanks.map((todo: TodoItem) => todo.id);
    const listId = selectedList?.id;
    
    // Optimistically update the todos cache for instant UI feedback
    if (queryClient && currentUser?.email) {
      queryClient.setQueryData(['todos', currentUser.email], (oldTodos: TodoItem[] = []) => {
        // Replace the reordered section in the todos array
        // Find all ids in this section
        const sectionIds = reorderedWithRanks.map(t => t.id);
        return oldTodos.map(todo => {
          const idx = sectionIds.indexOf(todo.id);
          if (idx !== -1) {
            return reorderedWithRanks[idx];
          }
          return todo;
        });
      });
    }
    reorderMutation.mutate({ todoIds, listId });
    
    // Clear activeDragId for within-section reordering
    setActiveDragId(null);
  };

  const handleDragCancel = () => {
    setDragOverInfo(null);
    setActiveDragId(null);
  };

  // Get the currently dragged todo for drag overlay
  const draggedTodo = activeDragId ? todoArray.find(t => t.id === Number(activeDragId)) : null;
  
  // Track successful cross-section moves to prevent overlay snap-back
  const [successfulMoveId, setSuccessfulMoveId] = useState<number | null>(null);
  
  useEffect(() => {
    if (successfulMoveId !== null) {
      const timeout = setTimeout(() => setSuccessfulMoveId(null), 100);
      return () => clearTimeout(timeout);
    }
  }, [successfulMoveId]);

  // Custom collision detection that prioritizes sections over individual todos
  const customCollisionDetection = (args: any) => {
    const { active, droppableContainers } = args;
    
    // Get all containers
    const sectionContainers = droppableContainers.filter((container: any) => 
      ['overdue', 'today', 'tomorrow', 'thisWeekend', 'later', 'noDueDate', 'completed'].includes(container.id)
    );
    
    const todoContainers = droppableContainers.filter((container: any) => 
      !['overdue', 'today', 'tomorrow', 'thisWeekend', 'later', 'noDueDate', 'completed'].includes(container.id)
    );
    
    // First, try to find todo collisions for precise within-section reordering
    const todoCollisions = closestCorners({
      ...args,
      droppableContainers: todoContainers
    });
    
    // If we have todo collisions, check if we're dragging within the same section
    if (todoCollisions.length > 0) {
      const activeId = Number(active.id);
      const overId = Number(todoCollisions[0].id);
      
      // Find which sections these todos belong to
      const findTodoSection = (todoId: number) => {
        const sectionKeys: (keyof typeof groupedTodos)[] = ['overdue', 'today', 'tomorrow', 'thisWeekend', 'later', 'noDueDate', 'completed'];
        for (const sectionKey of sectionKeys) {
          const sectionTodos = groupedTodos[sectionKey] || [];
          if (sectionTodos.find(t => t.id === todoId)) {
            return sectionKey;
          }
        }
        return null;
      };
      
      const activeSection = findTodoSection(activeId);
      const overSection = findTodoSection(overId);
      
      // If both todos are in the same section, use todo collision for precise reordering
      if (activeSection && overSection && activeSection === overSection) {
        return todoCollisions;
      }
    }
    
    // Otherwise, try section collisions for cross-section moves
    const sectionCollisions = pointerWithin({
      ...args,
      droppableContainers: sectionContainers
    });
    
    if (sectionCollisions.length > 0) {
      return sectionCollisions;
    }
    
    // Fallback to rectIntersection for sections
    const sectionRectCollisions = rectIntersection({
      ...args,
      droppableContainers: sectionContainers
    });
    
    if (sectionRectCollisions.length > 0) {
      return sectionRectCollisions;
    }
    
    // Final fallback to todo collisions if no sections detected
    return todoCollisions;
  };

  const content = (
    <div 
      className={`todo-list-container ${selectedList ? 'filtered-by-list' : ''} ${props.hideToolbar ? 'embedded-scroll' : ''}`}
      style={{ ...containerStyle, ...containerProps }}
    >
        {isLoading && (
          <div className="loading-container">
            <Loader type="bars" size="md" />
            <p>Loading todos...</p>
          </div>
        )}
        {error && (
          <div className="error-container">
            <div style={{ color: 'red', fontSize: 32, marginBottom: 8 }}>!</div>
            <p>Failed to load todos. Please try again.</p>
            <small>{error.message}</small>
          </div>
        )}
        {!isLoading && !error && (
          <>
            {!props.hideToolbar && (
              <div style={{ width: '100%' }}>
                <SuperTaskToolbar
                  onFilterChange={handleFilterChange}
                  currentFilters={filters}
                  selectedList={selectedList}
                  onCreateList={() => setShowCreateListForm(true)}
                  onEditList={uniqueEditListWrapper}
                />
              </div>
            )}

            {/* Chronological Task Sections with shared AnimatePresence for cross-section fly-in */}
            <div style={{ position: 'relative' }}>
              {activeTodos.length === 0 && completedTodos.length === 0 ? (
                <div className="todo-section">
                  <div className="empty-state">
                    <CheckCircle2 size={48} />
                    {hasActiveFilters ? (
                      <>
                        <h3>No tasks match your filters</h3>
                        <p style={{ marginBottom: '16px', lineHeight: 1.5 }}>
                          {getFilterSummaryJSX}. Try changing your filters, or clear them.
                        </p>
                        <Button
                          variant="light"
                          onClick={handleClearFilters}
                          style={{ marginBottom: '16px' }}
                        >
                          Clear all filters
                        </Button>
                      </>
                    ) : (
                      <>
                        <h3>Ready to get things done? 🚀</h3>
                        <p style={{ marginBottom: '16px' }}>
                          Your <span style={{ fontWeight: 600, color: 'var(--list-color, #3b82f6)' }}>{selectedList?.name || 'task list'}</span> is looking a bit lonely. Let's add your first task!
                        </p>
                      </>
                    )}
                    <SectionTodoEntry
                      sectionType="noDueDate"
                      currentFilters={filters}
                      onNewTodoCreated={props.onNewTodoCreated}
                      onFiltersChange={onFiltersChange}
                      hideIcon={true}
                    />
                  </div>
                </div>
              ) : (
                <TodoSectionWrapper
                  groupedTodos={groupedTodos}
                  filters={filters}
                  setEditingTodo={setEditingTodo}
                  highlightedTaskId={highlightedTaskId}
                  highlightMessage={highlightMessage}
                  onTaskInteraction={onTaskInteraction}
                  onTodoUpdate={onTodoUpdate}
                  sortType={sortType}
                  setSortType={setSortType}
                  lastDroppedId={lastDroppedId}
                  totalCompletedCount={allCompletedTodos.length}
                  completedFilter={filters.completed}
                  onCompletedFilterChange={(completedFilter) => handleFilterChange({ ...filters, completed: completedFilter })}
                  onNewTodoCreated={props.onNewTodoCreated}
                  onFiltersChange={onFiltersChange}
                  activeDragId={activeDragId}
                  dragOverInfo={dragOverInfo}
                  enableDnd={enableDnd}
                />
              )}
            </div>

            {/* Completed section is now handled by TodoSectionWrapper; remove duplicate rendering here */}

            <Modal
              isOpen={showCreateListForm}
              onClose={() => setShowCreateListForm(false)}
              title="Create New List"
              hideCloseButton={true}
              actionButtons={
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (createListData && createListData.name.trim() && createListData.color) {
                        createListMutation.mutate({
                          name: createListData.name,
                          description: createListData.description,
                          color: createListData.color,
                        });
                      }
                    }}
                    disabled={!createListData?.name.trim() || !createListData?.color || createListMutation.isPending}
                    className="header-btn header-btn-save"
                    title="Create list"
                    aria-label="Create list"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateListForm(false)}
                    className="header-btn header-btn-close"
                    title="Cancel"
                    aria-label="Cancel"
                  >
                    <X size={16} />
                  </button>
                </>
              }
            >
              <CreateListForm
                onSave={setCreateListData}
              />
            </Modal>

            {selectedList && (
              <Modal
                isOpen={showEditListForm}
                onClose={() => setShowEditListForm(false)}
                title="Edit List"
                hideCloseButton={true}
                actionButtons={
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (editListData && editListData.name.trim()) {
                          updateListMutation.mutate({
                            name: editListData.name,
                            description: editListData.description,
                            color: editListData.color,
                            isShared: editListData.isShared,
                            sharedWith: editListData.sharedWith,
                            isPublic: editListData.isPublic,
                          });
                        }
                      }}
                      disabled={!editListData?.name.trim() || updateListMutation.isPending}
                      className="header-btn header-btn-save"
                      title="Save changes"
                      aria-label="Save changes"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        deleteListMutation.mutate();
                      }}
                      disabled={deleteListMutation.isPending || selectedList?.isDefault}
                      className="header-btn header-btn-delete"
                      title={selectedList?.isDefault ? "Cannot delete default list" : "Delete list"}
                      aria-label="Delete list"
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEditListForm(false)}
                      className="header-btn header-btn-close"
                      title="Cancel"
                      aria-label="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </>
                }
              >
                <EditListForm
                  list={selectedList}
                  onSave={setEditListData}
                  onClose={() => setShowEditListForm(false)}
                />
              </Modal>
            )}

            {/* List Info Dialog for non-owners */}
            {selectedList && showListInfoDialog && (
              <ListInfoDialog
                list={selectedList}
                onClose={() => setShowListInfoDialog(false)}
              />
            )}

            {/* New Public URL Modal */}
            {newPublicUrl && (
              <Modal
                isOpen={!!newPublicUrl}
                onClose={() => setNewPublicUrl(null)}
                title="🚀 Public URL Generated!"
                hideCloseButton={false}
                actionButtons={
                  <button
                    type="button"
                    onClick={() => setNewPublicUrl(null)}
                    className="btn btn-primary"
                  >
                    Got it!
                  </button>
                }
              >
                <div className="new-public-url-modal">
                  <div className="success-message">
                    <div className="success-icon">🎉</div>
                    <p>Your list is now publicly shareable! Anyone with this link can view your list in read-only mode.</p>
                  </div>
                  
                  <div className="public-url-share-section">
                    <label>Public URL:</label>
                    <div className="public-url-share-display">
                      <input
                        type="text"
                        value={newPublicUrl}
                        readOnly
                        className="public-url-share-input"
                        onClick={(e) => e.currentTarget.select()}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary copy-url-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(newPublicUrl);
                          // Could add a toast notification here
                        }}
                        title="Copy to clipboard"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  <div className="share-tips">
                    <h4>💡 Sharing Tips:</h4>
                    <ul>
                      <li>Share this URL with anyone to give them read-only access</li>
                      <li>They can view all items, comments, and progress</li>
                      <li>You can disable public sharing anytime in list settings</li>
                    </ul>
                  </div>
                </div>
              </Modal>
            )}

            {/* Todo Edit Modal - renders at app level for full screen */}
            {editingTodo && (
              <Modal
                isOpen={!!editingTodo}
                onClose={() => {
                  setEditingTodo(null);
                  setEditFormData(null);
                }}
                title="Edit Task"
                hideCloseButton={true}
                actionButtons={
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (editFormData && editingTodo && editingTodo.id > 0) {
                          updateTodo.mutate({
                            id: editingTodo.id,
                            data: editFormData
                          }, {
                            onSuccess: () => {
                              setEditingTodo(null);
                              setEditFormData(null);
                            },
                          });
                        }
                      }}
                      disabled={!editFormData || !editFormData.title?.trim() || updateTodo.isPending || (editingTodo && editingTodo.id < 0)}
                      className="header-btn header-btn-save"
                      title={editingTodo && editingTodo.id < 0 ? "Please wait for task to be created..." : "Save task"}
                      aria-label="Save task"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (editingTodo) {
                          deleteTodo.mutate(editingTodo.id, {
                            onSuccess: () => {
                              setEditingTodo(null);
                              setEditFormData(null);
                            }
                          });
                        }
                      }}
                      disabled={deleteTodo.isPending}
                      className="header-btn header-btn-delete"
                      title="Delete task"
                      aria-label="Delete task"
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTodo(null);
                        setEditFormData(null);
                      }}
                      className="header-btn header-btn-close"
                      title="Cancel"
                      aria-label="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </>
                }
              >
                <EditTodoForm
                  todo={editingTodo}
                  onComplete={() => {
                    setEditingTodo(null);
                    setEditFormData(null);
                  }}
                  onDataChange={setEditFormData}
                />
              </Modal>
            )}
          </>
        )}
    </div>
  );

  if (!enableDnd) {
    return content;
  }

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      measuring={measuring}
      onDragStart={handleDragStart} 
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {content}
      
      {/* Drag overlay for visual feedback during drag */}
      <DragOverlay>
        {draggedTodo && activeDragId && (
          <div style={{ 
            transform: 'rotate(1deg)', // Reduced rotation from 5° to 1°
            opacity: 0.95,
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          }}>
            {/* Use the exact TodoItemComponent for the drag overlay */}
            <TodoItemComponent
              todo={draggedTodo}
              onEdit={() => {}} // No-op for drag overlay
              dragHandleProps={null}
              dragAttributes={null}
              dragListeners={null}
              sortType="rank"
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
